// v2-rebuild
// ══════════════════════════════════════════════════════
// Trade Aggregate Panel (Phase 2e-A)
// Aggregate-Sicht über mehrere Units (Opt: 30 Trials / WF: 15 Windows).
// Drei Ebenen: kombinierte Stats · rule-based Insights · Units-Vergleichstabelle.
// Wiederverwendet SummaryMetrics, TradeInsights, SortTh.
// ══════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from "react";
import { theme, fonts } from "../theme";
import { API_BASE } from "../config";
import { SummaryMetrics } from "./TradeCharts";
import TradeInsights from "./TradeInsights";
import { SortTh, sortRows } from "./shared";
import { formatPnl, Pill } from "./ui";

// Layout-Helper (identisch zu TradeAnalysisPanel für konsistentes Look)
const SubCard = ({ title, children, style }) => (
  <div style={{
    background: theme.white,
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: "14px 18px",
    ...style,
  }}>
    {title && <div style={{
      fontSize: 11, fontWeight: 700, color: theme.text2,
      textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 12,
    }}>{title}</div>}
    {children}
  </div>
);

const EmptyBanner = ({ msg, hint }) => (
  <div style={{
    padding: "20px 24px",
    background: theme.amberLight,
    border: `1px solid rgba(217,119,6,0.2)`,
    borderRadius: 12,
    fontSize: 13,
    color: theme.amberText,
  }}>
    <div style={{ fontWeight: 700, marginBottom: 4 }}>ℹ️ {msg}</div>
    {hint && <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>{hint}</div>}
  </div>
);

const LoadingSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <div style={{ height: 100, background: theme.tint, borderRadius: 10 }} className="sl-shimmer" />
    <div style={{ height: 240, background: theme.tint, borderRadius: 10 }} className="sl-shimmer" />
    <div style={{ height: 320, background: theme.tint, borderRadius: 10 }} className="sl-shimmer" />
  </div>
);

// Compact dollar formatter (lokal für Units-Tabelle)
const dollar = (v, d = 0) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 10000) return `${n < 0 ? "-" : ""}$${(abs / 1000).toFixed(1)}k`;
  return `${n < 0 ? "-" : ""}$${abs.toFixed(d)}`;
};

// ═════════════════════════════════════════════════════════
// Units-Tabelle (pro Trial oder Window eine Zeile)
// ═════════════════════════════════════════════════════════
function UnitsTable({ units, runType, sortMode }) {
  // Mapping: externe Top-30-Basis (sortMode) -> interne Spalten-Sort-Keys
  const sortModeToKey = {
    composite:     "composite_score",
    pnl:           "total_pnl",
    sharpe:        "sharpe",
    drawdown:      "max_drawdown",
    profit_factor: "profit_factor",
  };
  const [sort, setSort] = useState({
    key: runType === "opt" ? (sortModeToKey[sortMode] || "composite_score") : "unit_id",
    asc: false,
  });

  // Wenn die externen Top-30-Basis Buttons (sortMode) wechseln, internen
  // Spalten-Sort entsprechend updaten. User kann danach Spalten manuell klicken.
  useEffect(() => {
    if (runType !== "opt") return;
    const newKey = sortModeToKey[sortMode];
    if (newKey) {
      // drawdown: ascending (klein = gut), alle anderen descending (groß = gut)
      setSort({ key: newKey, asc: sortMode === "drawdown" });
    }
  }, [sortMode, runType]);

  const sorted = useMemo(() => sortRows(units, sort), [units, sort]);

  if (!units || units.length === 0) {
    return <div style={{ padding: 16, textAlign: "center", color: theme.text4,
                          fontSize: 12, fontStyle: "italic" }}>
      Keine Units zum Vergleich
    </div>;
  }

  // Aggregate-Reihen-Hervorhebung: Top 3 nach PnL grün, schlechteste 2 rot (visueller Anker)
  const pnlSorted = [...units].sort((a,b) => (b.total_pnl||0) - (a.total_pnl||0));
  const topIds = new Set(pnlSorted.slice(0, 3).map(u => u.unit_id));
  const bottomIds = new Set(pnlSorted.slice(-2).filter(u => (u.total_pnl||0) < 0).map(u => u.unit_id));

  return (
    <div style={{ maxHeight: 480, overflow: "auto", border: `1px solid ${theme.border}`,
                   borderRadius: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${theme.divider}`,
                        position: "sticky", top: 0, background: theme.white, zIndex: 1 }}>
            <SortTh sortKey="unit_id"     sort={sort} setSort={setSort}>{runType === "opt" ? "Trial" : "Window"}</SortTh>
            {runType === "opt" && (
              <>
                <SortTh sortKey="grade" sort={sort} setSort={setSort}>Grade</SortTh>
                <SortTh sortKey="composite_score" sort={sort} setSort={setSort}>Kombi</SortTh>
              </>
            )}
            {runType === "wf" && (
              <th style={{ padding: "7px 10px", fontSize: 9, fontWeight: 700, color: theme.text4,
                            textTransform: "uppercase", textAlign: "left", whiteSpace: "nowrap" }}>Period</th>
            )}
            <SortTh sortKey="trade_count"   sort={sort} setSort={setSort}>Trades</SortTh>
            <SortTh sortKey="total_pnl"     sort={sort} setSort={setSort}>P&L</SortTh>
            <SortTh sortKey="sharpe"        sort={sort} setSort={setSort}>Sharpe</SortTh>
            <SortTh sortKey="max_drawdown"  sort={sort} setSort={setSort}>DD%</SortTh>
            <SortTh sortKey="win_rate"      sort={sort} setSort={setSort}>WR</SortTh>
            <SortTh sortKey="profit_factor" sort={sort} setSort={setSort}>PF</SortTh>
            <SortTh sortKey="expectancy"    sort={sort} setSort={setSort}>Exp</SortTh>
            <SortTh sortKey="sl_share"      sort={sort} setSort={setSort}>SL%</SortTh>
            <SortTh sortKey="be_share"      sort={sort} setSort={setSort}>BE%</SortTh>
            <SortTh sortKey="trail_share"   sort={sort} setSort={setSort}>Trail%</SortTh>
            <SortTh sortKey="mfe_mae_ratio" sort={sort} setSort={setSort}>MFE/MAE</SortTh>
            {runType === "wf" && (
              <th style={{ padding: "7px 10px", fontSize: 9, fontWeight: 700, color: theme.text4,
                            textTransform: "uppercase", textAlign: "center" }}>Rel.</th>
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.map(u => {
            const isTop = topIds.has(u.unit_id);
            const isBottom = bottomIds.has(u.unit_id);
            const rowBg = isTop ? "rgba(22,163,74,0.04)"
                        : isBottom ? "rgba(220,38,38,0.03)"
                        : "transparent";
            return (
              <tr key={u.unit_id} className="sl-row"
                  style={{ borderBottom: `1px solid ${theme.divider}`, background: rowBg }}>
                <td style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700,
                              fontFamily: fonts.mono }}>
                  {u.is_best && <span style={{ color: theme.amber, marginRight: 3 }}>★</span>}
                  {runType === "opt" ? `#${u.unit_id}` : `W${u.unit_id}`}
                </td>
                {runType === "opt" && (
                  <>
                    <td style={{ padding: "5px 10px" }}>
                      {u.grade && (
                        <Pill
                          bg={u.grade === "A" ? theme.greenLight : u.grade === "B" ? theme.blueLight : theme.amberLight}
                          color={u.grade === "A" ? theme.greenText : u.grade === "B" ? theme.blueText : theme.amberText}>
                          {u.grade}
                        </Pill>
                      )}
                    </td>
                    <td style={{ padding: "5px 10px", fontSize: 12, fontFamily: fonts.mono,
                                  fontWeight: 700,
                                  color: (u.composite_score||0) > 0.5 ? theme.greenText : theme.text3 }}>
                      {u.composite_score != null ? Number(u.composite_score).toFixed(2) : u.score != null ? Number(u.score).toFixed(2) : "—"}
                    </td>
                  </>
                )}
                {runType === "wf" && (
                  <td style={{ padding: "5px 10px", fontSize: 9.5, fontFamily: fonts.mono,
                                color: theme.text3, whiteSpace: "nowrap" }}>
                    {u.oos_start?.slice(5)} → {u.oos_end?.slice(5)}
                  </td>
                )}
                <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono,
                              color: theme.text3 }}>
                  {u.trade_count}
                  {u.has_trade_details === false && (
                    <span title="Keine Trade-Details persistiert (alter Run vor Multi-Metric-Persist)"
                          style={{ marginLeft: 4, color: theme.text4, fontSize: 10 }}>°</span>
                  )}
                </td>
                <td style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700, fontFamily: fonts.mono,
                              color: (u.total_pnl || 0) >= 0 ? theme.greenText : theme.redText }}>
                  {formatPnl(u.total_pnl)}
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono,
                              color: u.sharpe >= 1.5 ? theme.greenText
                                    : u.sharpe >= 0.8 ? theme.amberText
                                    : u.sharpe != null ? theme.text3 : theme.text4 }}>
                  {u.sharpe == null ? "—" : Number(u.sharpe).toFixed(2)}
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono,
                              color: u.max_drawdown == null ? theme.text4
                                    : u.max_drawdown <= 5 ? theme.greenText
                                    : u.max_drawdown <= 10 ? theme.amberText
                                    : theme.redText }}>
                  {u.max_drawdown == null ? "—" : Number(u.max_drawdown).toFixed(1) + "%"}
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono }}>
                  {Number(u.win_rate || 0).toFixed(1)}%
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono,
                              color: u.profit_factor >= 1.5 ? theme.greenText
                                    : u.profit_factor >= 1.0 ? theme.amberText
                                    : u.profit_factor != null ? theme.redText : theme.text4 }}>
                  {u.profit_factor == null ? "∞" : Number(u.profit_factor).toFixed(2)}
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono }}>
                  {dollar(u.expectancy, 2)}
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono,
                              color: u.sl_share > 35 ? theme.redText : theme.text3 }}>
                  {Number(u.sl_share || 0).toFixed(0)}%
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono,
                              color: u.be_share > 30 ? theme.amberText : theme.text3 }}>
                  {Number(u.be_share || 0).toFixed(0)}%
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono,
                              color: u.trail_share > 25 ? theme.greenText : theme.text3 }}>
                  {Number(u.trail_share || 0).toFixed(0)}%
                </td>
                <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono,
                              color: u.mfe_mae_ratio > 2 ? theme.greenText
                                    : u.mfe_mae_ratio < 1.2 ? theme.redText : theme.text3 }}>
                  {u.mfe_mae_ratio == null ? "—" : u.mfe_mae_ratio.toFixed(2)}
                </td>
                {runType === "wf" && (
                  <td style={{ padding: "5px 10px", textAlign: "center" }}>
                    {u.is_reliable === true ? <span style={{ color: theme.greenText }}>✓</span>
                     : u.is_reliable === false ? <span style={{ color: theme.redText }}>✗</span>
                     : <span style={{ color: theme.text4 }}>—</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// MAIN PANEL
// ═════════════════════════════════════════════════════════
export default function TradeAggregatePanel({ runType, runId }) {
  const [segment, setSegment] = useState("oos");  // nur WF
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sortMode, setSortMode] = useState("composite");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!runId || !runType) return;
    setLoading(true); setError(null); setData(null);
    // Backend handelt Sortierung UND Qualitäts-Filter. Toggle "showAll" überspringt Filter.
    // Cache-Buster + no-store verhindert Cloudflare/Browser-Cache.
    const url = runType === "opt"
      ? `${API_BASE}/analyze/opt/${runId}/trades_aggregate?sort_by=${sortMode}&show_all=${showAll}&_t=${Date.now()}`
      : `${API_BASE}/analyze/wf/${runId}/trades_aggregate?segment=${segment}&_t=${Date.now()}`;
    fetch(url, { cache: "no-store" })
      .then(r => r.json())
      .then(j => { setData(j); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [runType, runId, segment, sortMode, showAll]);

  if (error) {
    return <EmptyBanner msg={`Fehler beim Laden: ${error}`} />;
  }
  if (loading) return <LoadingSkeleton />;
  if (!data) return null;
  if (data.no_trade_data) {
    return <EmptyBanner msg={data.reason || "Keine Trade-Daten für Aggregate."}
                        hint="Neuer Run nach Phase-1-Deploy erforderlich." />;
  }

  const agStats = data.aggregate?.stats;
  const units = data.units || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Segment-Toggle für WF */}
      {runType === "wf" && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: theme.text3, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.3px" }}>
            Segment:
          </span>
          <div style={{ display: "flex", gap: 3, padding: 3, borderRadius: 7,
                        background: theme.tint }}>
            {["oos", "is"].map(s => (
              <button key={s}
                onClick={() => setSegment(s)}
                style={{
                  padding: "4px 12px", borderRadius: 5, border: "none", cursor: "pointer",
                  fontSize: 11, fontWeight: segment === s ? 700 : 500,
                  background: segment === s ? theme.white : "transparent",
                  color: segment === s ? theme.text : theme.text3,
                  boxShadow: segment === s ? theme.shadow1 : "none",
                  textTransform: "uppercase",
                }}>
                {s}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 10, color: theme.text4, fontStyle: "italic" }}>
            {segment === "oos" ? "echtes Out-of-Sample" : "In-Sample (typisch optimistisch)"}
          </span>
          <span style={{ marginLeft: "auto", fontSize: 10, color: theme.text3, fontFamily: fonts.mono }}>
            {data.meta?.units_count} Windows · {data.meta?.total_trades} Trades
            {data.meta?.reliable_windows != null && ` · ${data.meta.reliable_windows} reliable`}
          </span>
        </div>
      )}

      {runType === "opt" && (
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <span style={{ fontSize:12, fontWeight:700, color:theme.text3 }}>Top-30 Basis:</span>
            {[
              ["composite", "⚖️ Kombi", "Perzentil-Rang über alle Metriken"],
              ["pnl",       "💰 Max P&L", "Höchste absolute Gewinne"],
              ["sharpe",    "📈 Sharpe", "Bestes Risiko/Rendite-Verhältnis"],
              ["drawdown",  "🛡️ Min DD", "Geringster maximaler Drawdown"],
              ["profit_factor", "⭐ PF", "Höchster Profit Factor"],
            ].map(([key, label, title]) => (
              <button key={key} onClick={() => setSortMode(key)} title={title}
                style={{ padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:sortMode===key?700:400,
                  border:`1px solid ${sortMode===key?theme.blueText:theme.border}`,
                  background:sortMode===key?theme.blueLight:theme.white,
                  color:sortMode===key?theme.blueText:theme.text3, cursor:"pointer" }}>
                {label}
              </button>
            ))}
            <label style={{ display:"flex", alignItems:"center", gap:5, marginLeft:"auto",
                            fontSize:11, color:theme.text3, cursor:"pointer", userSelect:"none" }}
                   title="Default: nur Trials mit min 30 Trades, PnL>0, PF≥1.2, DD≤20%, Grade A/B/C. Aktivieren zeigt alle nicht-disqualifizierten Trials.">
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)}
                     style={{ cursor:"pointer" }} />
              Alle Trials zeigen (Filter aus)
            </label>
          </div>
          {data.filter_info && (
            <div style={{ fontSize:10.5, color:theme.text4, fontFamily:fonts.mono,
                          background:theme.tint, padding:"6px 10px", borderRadius:5,
                          display:"flex", gap:14, flexWrap:"wrap" }}>
              <span><b style={{color:theme.text3}}>Eingabe:</b> {data.filter_info.total_input} Trials</span>
              <span><b style={{color:theme.text3}}>Nicht disqualifiziert:</b> {data.filter_info.not_disqualified}</span>
              {!showAll && data.filter_info.hard_floor_passed != null && (
                <span title="min 30 Trades, PnL>0, PF≥1.2, DD≤20%">
                  <b style={{color:theme.text3}}>Hard Floor:</b> {data.filter_info.hard_floor_passed}
                </span>
              )}
              {!showAll && data.filter_info.tier && (
                <span><b style={{color:theme.text3}}>Grade-Tier:</b> {data.filter_info.tier}</span>
              )}
              <span style={{color:theme.greenText, fontWeight:700}}>
                → {data.units?.length || 0} angezeigt
              </span>
              {data.meta?.trials_without_details > 0 && (
                <span style={{color:theme.amberText}}
                      title="Ohne persistierte Trade-Details. Neuer Optimizer-Lauf liefert mehr.">
                  ° {data.meta.trials_without_details} ohne Detail-Trades
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {runType === "wf" && (
        <div style={{ fontSize: 10, color: theme.text3, fontFamily: fonts.mono, marginBottom:8 }}>
          {data.meta?.units_count} Windows · {data.meta?.total_trades} Trades kombiniert
          {data.meta?.best_trial != null && ` · ★ Best Trial: #${data.meta.best_trial}`}
        </div>
      )}

      {/* Aggregate-Summary (kombinierte Stats) */}
      {agStats?.summary && (
        <SubCard title="Kombinierte Kennzahlen (alle Units)">
          <SummaryMetrics summary={agStats.summary} />
        </SubCard>
      )}

      {/* Aggregate-Insights — Stats-basierte Regeln greifen,
          Streak/Plateau-Regeln werden automatisch übersprungen (keine einzelnen Trades mitgesendet) */}
      {agStats && (
        <SubCard title="📋 Aggregate-Erkenntnisse (rule-based, über alle Units)">
          <TradeInsights stats={agStats} trades={[]} />
        </SubCard>
      )}

      {/* Units-Vergleichstabelle */}
      <SubCard title={`Units-Vergleich · ${units.length} ${runType === "opt" ? "Trials" : "Windows"}`}>
        <div style={{ fontSize: 10, color: theme.text4, marginBottom: 10, fontStyle: "italic" }}>
          Farbhinweise: <span style={{ color: theme.redText }}>rote</span> SL%/BE%
          oder <span style={{ color: theme.redText }}>rote</span> MFE/MAE = Problem-Schwelle.
          Hintergrundfarbe: Top-3 nach PnL grünlich, Worst-2 rötlich.
        </div>
        <UnitsTable units={units} runType={runType} sortMode={sortMode} />
      </SubCard>
    </div>
  );
}
